import React, { useState, useCallback } from 'react';
import logger from '../../utils/logger';
import { useMasterData } from '../../hooks/useMasterData';
// FL-259: WorkingTimeSlot は working_days 処理用
// FL-270: StationDetails は最寄駅機能用
import { LandTransporter, Role, WorkingTimeSlot } from '../../types';
import type { StationDetails } from '../../types/maps';
import { UsersIcon, PlusCircleIcon, ListBulletIcon, TrashIcon } from '@heroicons/react/24/outline';
import { isDemoMode } from '../../utils/environment';
import { extractStationName } from '../../utils/stationHelpers';
import { Button, PageTitle, PaginatedTable, PaginatedCards, Alert } from '../../components/ui';
// import { TimeSelector } from '../../components/ui/TimeSelector';
// import { DEFAULT_WORKING_HOURS_SETTINGS } from '../../utils/timePickerDefaults';
import LandTransporterDetailModal from '../../components/master/LandTransporterDetailModal';
import { LandTransporterForm } from '../../components/forms/LandTransporterForm';
import { useAuthorization } from '../../hooks/useAuthorization';

// ステータススタイル取得関数 - 現在未使用
// const getStatusClass = (status: LandTransporter['status']) => {
//     switch (status) {
//         case 'Available': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
//         case 'Busy': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
//         case 'Unavailable': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
//         default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
//     }
// };

// ステータステキスト取得関数 - 現在未使用
// const getStatusText = (status: LandTransporter['status']) => {
//     switch (status) {
//         case 'Available': return '対応可能';
//         case 'Busy': return '稼働中';
//         case 'Unavailable': return '対応不可';
//         default: return '不明';
//     }
// };

export const LandTransporterListPage: React.FC = () => {
  const isDemo = isDemoMode();
  const { checkPermission } = useAuthorization();

  // FL-180: 業務制約の参照権限チェック（SUPER_ADMIN, ADMIN, DISPATCHERのみ）
  const canViewBusinessConstraints = checkPermission([
    Role.SUPER_ADMIN,
    Role.ADMIN,
    Role.DISPATCHER,
  ]);

  const {
    landTransporters,
    vehicleTypes,
    addLandTransporter,
    updateLandTransporter,
    removeLandTransporter,
    loading,
  } = useMasterData();
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingTransporter, setEditingTransporter] = useState<LandTransporter | null>(null);
  const [selectedTransporter, setSelectedTransporter] = useState<LandTransporter | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    transporter: LandTransporter;
  } | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const initialFormState = {
    name: '',
    phone: '',
    address: '',
    drivableVehicleTypes: [] as string[],
    // FL-563: workingDaysをデフォルト値で初期化（段階的削除中）
    workingDays: {
      monday: null as WorkingTimeSlot | null,
      tuesday: null as WorkingTimeSlot | null,
      wednesday: null as WorkingTimeSlot | null,
      thursday: null as WorkingTimeSlot | null,
      friday: null as WorkingTimeSlot | null,
      saturday: null as WorkingTimeSlot | null,
      sunday: null as WorkingTimeSlot | null,
    },
    hasLicenseForLargeVehicles: false,
    canWorkNightShift: false,
    canWorkLongDistance: false,
    hasTemporaryLicense: false,
    isATOnly: false,
    notes: '',
    // FL-180: 業務制約フラグ
    isSolicitationProhibited: false,
    isDesignatedStoreProhibited: false,
    // FL-270: 最寄駅情報（Google Maps API統合）
    nearestStation: '',
    nearestStationPlaceId: '',
    nearestStationLat: 0,
    nearestStationLng: 0,
  };
  const [newTransporterData, setNewTransporterData] = useState(initialFormState);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value, type } = e.target;
      if (type === 'checkbox') {
        const checked = (e.target as HTMLInputElement).checked;
        setNewTransporterData(prev => ({ ...prev, [name]: checked }));
      } else {
        setNewTransporterData(prev => ({ ...prev, [name]: value }));
      }
    },
    []
  );

  // FL-259: handleWorkingDayToggle, handleWorkingTimeChangeは削除（working_days廃止）

  const handleVehicleTypeChange = (vehicleTypeName: string, checked: boolean) => {
    setNewTransporterData(prev => ({
      ...prev,
      drivableVehicleTypes: checked
        ? [...prev.drivableVehicleTypes, vehicleTypeName]
        : prev.drivableVehicleTypes.filter(name => name !== vehicleTypeName),
    }));
  };

  // FL-270: 最寄駅選択時のハンドラー
  const handleStationSelect = useCallback((stationName: string, stationDetails: StationDetails) => {
    setNewTransporterData(prev => ({
      ...prev,
      nearestStation: stationName,
      nearestStationPlaceId: stationDetails.placeId,
      nearestStationLat: stationDetails.latitude,
      nearestStationLng: stationDetails.longitude,
    }));
    logger.info('[LandTransporterListPage] 最寄駅を選択', {
      stationName,
      latitude: stationDetails.latitude,
      longitude: stationDetails.longitude,
    });
  }, []);

  // 編集モードを開始
  const startEditing = (transporter: LandTransporter) => {
    setEditingTransporter(transporter);
    setNewTransporterData({
      name: transporter.name,
      phone: transporter.phone,
      address: transporter.address,
      drivableVehicleTypes: [...transporter.drivableVehicleTypes],
      // FL-563: workingDaysをコピー（段階的削除中）
      workingDays: transporter.workingDays || {
        monday: null as WorkingTimeSlot | null,
        tuesday: null as WorkingTimeSlot | null,
        wednesday: null as WorkingTimeSlot | null,
        thursday: null as WorkingTimeSlot | null,
        friday: null as WorkingTimeSlot | null,
        saturday: null as WorkingTimeSlot | null,
        sunday: null as WorkingTimeSlot | null,
      },
      hasLicenseForLargeVehicles: transporter.hasLicenseForLargeVehicles,
      canWorkNightShift: transporter.canWorkNightShift,
      canWorkLongDistance: transporter.canWorkLongDistance,
      hasTemporaryLicense: transporter.hasTemporaryLicense,
      isATOnly: transporter.isATOnly,
      // FL-180: 業務制約フラグ
      isSolicitationProhibited: transporter.isSolicitationProhibited,
      isDesignatedStoreProhibited: transporter.isDesignatedStoreProhibited,
      // FL-270: 最寄駅情報
      nearestStation: transporter.nearestStation || '',
      nearestStationPlaceId: transporter.nearestStationPlaceId || '',
      nearestStationLat: transporter.nearestStationLat || 0,
      nearestStationLng: transporter.nearestStationLng || 0,
      notes: transporter.notes || '',
    });
    setIsFormVisible(true);
  };

  // 編集モードをキャンセル
  const cancelEditing = () => {
    setEditingTransporter(null);
    setNewTransporterData(initialFormState);
    setIsFormVisible(false);
    setValidationError(null);
  };

  // フォーム送信処理
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (!newTransporterData.name) {
      setValidationError('氏名を入力してください。');
      return;
    }

    if (newTransporterData.drivableVehicleTypes.length === 0) {
      setValidationError('運転可能車種を最低1つ選択してください。');
      return;
    }

    // FL-259: workingDaysバリデーションは削除（driver_schedules移行）

    try {
      if (editingTransporter) {
        // 編集モード
        await updateLandTransporter(editingTransporter.id, {
          ...newTransporterData,
          status: editingTransporter.status, // ステータスは保持
        });
      } else {
        // 新規追加モード
        // FL-563: workingDaysをデフォルト値で初期化（段階的削除中）
        await addLandTransporter({
          id: crypto.randomUUID(), // ID生成を追加
          ...newTransporterData,
          status: 'Available', // 新規は対応可能に設定
          workingDays: newTransporterData.workingDays || {
            monday: null as WorkingTimeSlot | null,
            tuesday: null as WorkingTimeSlot | null,
            wednesday: null as WorkingTimeSlot | null,
            thursday: null as WorkingTimeSlot | null,
            friday: null as WorkingTimeSlot | null,
            saturday: null as WorkingTimeSlot | null,
            sunday: null as WorkingTimeSlot | null,
          },
        } as LandTransporter);
      }

      // フォームリセット
      setIsFormVisible(false);
      setEditingTransporter(null);
      setNewTransporterData(initialFormState);
      setValidationError(null);
    } catch (error) {
      logger.error('陸送員の保存に失敗しました:', error);
      setValidationError('保存に失敗しました。もう一度お試しください。');
    }
  };

  // 削除処理
  const handleDelete = async (transporter: LandTransporter) => {
    setDeleteConfirm({ show: true, transporter });
  };

  const confirmDelete = async () => {
    if (deleteConfirm) {
      try {
        await removeLandTransporter(deleteConfirm.transporter.id);
        setDeleteConfirm(null);
      } catch (error) {
        logger.error('陸送員の削除に失敗しました:', error);
        alert('削除に失敗しました。もう一度お試しください。');
      }
    }
  };

  const TransporterList = () => (
    <div>
      {/* デモモード制限通知 */}
      {isDemo && (
        <div className='mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg'>
          <div className='flex items-center'>
            <UsersIcon className='h-5 w-5 text-blue-600 dark:text-blue-400 mr-2' />
            <p className='text-blue-700 dark:text-blue-300'>
              デモ体験では、陸送員の追加機能は無効化されています。
            </p>
          </div>
        </div>
      )}

      <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0'>
        <h2 className='text-lg sm:text-xl font-semibold text-gray-700 dark:text-white flex items-center'>
          <ListBulletIcon className='h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3' />
          陸送員一覧 ({landTransporters.length})
        </h2>
        <Button
          onClick={() => {
            if (isDemo) return;
            setEditingTransporter(null);

            // デフォルト車種を設定（SUV、コンパクトカー、セダン、ミニバン、軽自動車、ハッチバック）
            const defaultVehicleTypeNames = [
              'SUV',
              'コンパクトカー',
              'セダン',
              'ミニバン',
              '軽自動車',
              'ハッチバック',
            ];
            const defaultVehicleTypeIds = defaultVehicleTypeNames
              .map(name => vehicleTypes.find(vt => vt.name === name)?.id)
              .filter((id): id is string => id !== undefined);

            setNewTransporterData({
              ...initialFormState,
              drivableVehicleTypes: defaultVehicleTypeIds,
            });
            setIsFormVisible(true);
          }}
          disabled={isDemo}
          variant='primary'
          size='md'
          className={`w-full sm:w-auto ${isDemo ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={isDemo ? 'デモモードでは使用できません' : ''}
        >
          <PlusCircleIcon className='h-5 w-5 mr-2' />
          <span className='hidden sm:inline'>新規陸送員を追加</span>
          <span className='sm:hidden'>新規追加</span>
        </Button>
      </div>

      {landTransporters.length === 0 ? (
        <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center'>
          <div className='text-gray-500 dark:text-gray-400'>登録されている陸送員がいません</div>
        </div>
      ) : (
        <div>
          {/* デスクトップ表示：テーブル */}
          <div className='hidden lg:block'>
            <PaginatedTable
              data={[...landTransporters].sort((a, b) => a.name.localeCompare(b.name, 'ja'))}
              loading={loading}
              storageKey='landTransporterList'
              onRowClick={transporter => setSelectedTransporter(transporter)}
              columns={[
                {
                  key: 'name',
                  label: '氏名',
                  render: (transporter: LandTransporter) => (
                    <div>
                      <div className='font-medium text-gray-900 dark:text-white text-sm'>
                        {transporter.name}
                      </div>
                      <div className='text-xs text-gray-500 dark:text-gray-400 truncate'>
                        {transporter.address}
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'nearestStation',
                  label: '最寄駅',
                  render: (transporter: LandTransporter) => (
                    <div className='text-sm text-gray-700 dark:text-gray-300'>
                      {extractStationName(transporter.nearestStation)}
                    </div>
                  ),
                },
                {
                  key: 'vehicleTypes',
                  label: '運転可能車種',
                  render: (transporter: LandTransporter) => (
                    <div className='flex flex-wrap gap-1 max-w-xs'>
                      {transporter.drivableVehicleTypes.slice(0, 3).map(typeName => {
                        const vehicleType = vehicleTypes.find(vt => vt.name === typeName);
                        return (
                          <span
                            key={typeName}
                            className='px-1.5 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 text-xs rounded'
                          >
                            {vehicleType?.name || typeName}
                          </span>
                        );
                      })}
                      {transporter.drivableVehicleTypes.length > 3 && (
                        <span className='text-xs text-gray-500 dark:text-gray-400'>
                          +{transporter.drivableVehicleTypes.length - 3}
                        </span>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'features',
                  label: '特記事項',
                  render: (transporter: LandTransporter) => {
                    const features = [];
                    const constraints = [];
                    if (transporter.hasLicenseForLargeVehicles) features.push('大型免許');
                    if (transporter.canWorkNightShift) features.push('夜間可');
                    if (transporter.canWorkLongDistance) features.push('長距離可');
                    if (transporter.hasTemporaryLicense) features.push('仮ナンバー有');
                    if (transporter.isATOnly) features.push('AT限定');
                    // FL-180: 業務制約は権限チェック
                    if (canViewBusinessConstraints) {
                      if (transporter.isSolicitationProhibited) constraints.push('客引きNG');
                      if (transporter.isDesignatedStoreProhibited) constraints.push('指定店舗NG');
                    }

                    const totalItems = features.length + constraints.length;

                    return totalItems > 0 ? (
                      <div className='flex flex-wrap gap-1 max-w-xs'>
                        {features.slice(0, 2).map((feature, index) => (
                          <span
                            key={index}
                            className='px-1.5 py-0.5 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300 text-xs rounded'
                          >
                            {feature}
                          </span>
                        ))}
                        {constraints.map((constraint, index) => (
                          <span
                            key={`constraint-${index}`}
                            className='px-1.5 py-0.5 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 text-xs rounded'
                          >
                            {constraint}
                          </span>
                        ))}
                        {totalItems > 2 && (
                          <span className='text-xs text-gray-500 dark:text-gray-400'>
                            +{totalItems - 2}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className='text-xs text-gray-500 dark:text-gray-400'>-</span>
                    );
                  },
                },
              ]}
              rowKey='id'
              emptyMessage='登録されている陸送員がいません'
            />
          </div>

          {/* モバイル表示：カード */}
          <div className='lg:hidden'>
            <PaginatedCards
              data={[...landTransporters].sort((a, b) => a.name.localeCompare(b.name, 'ja'))}
              loading={loading}
              storageKey='landTransporterList'
              keyExtractor='id'
              emptyMessage='登録されている陸送員がいません'
              renderCard={transporter => (
                <div
                  className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-5 card-hover card-touch touch-target fade-in cursor-pointer'
                  onClick={() => setSelectedTransporter(transporter)}
                >
                  <div className='mb-4'>
                    <h3 className='text-base sm:text-lg font-bold text-gray-800 dark:text-white truncate'>
                      {transporter.name}
                    </h3>
                    <p className='text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate'>
                      {transporter.address}
                    </p>
                  </div>

                  <div className='space-y-3'>
                    <div>
                      <span className='text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider'>
                        運転可能車種
                      </span>
                      <div className='flex flex-wrap gap-1 mt-1'>
                        {transporter.drivableVehicleTypes.map(typeName => {
                          const vehicleType = vehicleTypes.find(vt => vt.name === typeName);
                          return vehicleType ? (
                            <span
                              key={typeName}
                              className='px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 text-xs rounded'
                            >
                              {vehicleType.name}
                            </span>
                          ) : (
                            <span
                              key={typeName}
                              className='px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 text-xs rounded'
                            >
                              {typeName}
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    {transporter.nearestStation && (
                      <div>
                        <span className='text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider'>
                          最寄駅
                        </span>
                        <p className='text-sm text-gray-900 dark:text-white mt-1'>
                          {extractStationName(transporter.nearestStation)}
                        </p>
                      </div>
                    )}

                    {(transporter.hasLicenseForLargeVehicles ||
                      transporter.canWorkNightShift ||
                      transporter.canWorkLongDistance ||
                      transporter.hasTemporaryLicense ||
                      transporter.isATOnly ||
                      (canViewBusinessConstraints &&
                        (transporter.isSolicitationProhibited ||
                          transporter.isDesignatedStoreProhibited))) && (
                      <div>
                        <span className='text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider'>
                          特記事項
                        </span>
                        <div className='flex flex-wrap gap-1 mt-1'>
                          {transporter.hasLicenseForLargeVehicles && (
                            <span className='px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300 text-xs rounded'>
                              大型免許
                            </span>
                          )}
                          {transporter.canWorkNightShift && (
                            <span className='px-2 py-1 bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300 text-xs rounded'>
                              夜間可
                            </span>
                          )}
                          {transporter.canWorkLongDistance && (
                            <span className='px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 text-xs rounded'>
                              長距離可
                            </span>
                          )}
                          {transporter.hasTemporaryLicense && (
                            <span className='px-2 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 text-xs rounded'>
                              仮ナンバー有
                            </span>
                          )}
                          {transporter.isATOnly && (
                            <span className='px-2 py-1 bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300 text-xs rounded'>
                              AT限定
                            </span>
                          )}
                          {/* FL-180: 業務制約は権限チェック */}
                          {canViewBusinessConstraints && transporter.isSolicitationProhibited && (
                            <span className='px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 text-xs rounded'>
                              客引きNG
                            </span>
                          )}
                          {canViewBusinessConstraints &&
                            transporter.isDesignatedStoreProhibited && (
                              <span className='px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 text-xs rounded'>
                                指定店舗NG
                              </span>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <PageTitle icon={UsersIcon} title='陸送員管理' />

      {isFormVisible ? (
        <div>
          {validationError && (
            <Alert variant='error' dismissible={true} onDismiss={() => setValidationError(null)}>
              {validationError}
            </Alert>
          )}
          <LandTransporterForm
            formData={newTransporterData}
            editingTransporter={editingTransporter}
            vehicleTypes={vehicleTypes}
            canEditBusinessConstraints={canViewBusinessConstraints}
            onInputChange={handleInputChange}
            onVehicleTypeChange={handleVehicleTypeChange}
            // FL-259: onWorkingDayToggle, onWorkingTimeChangeは削除
            // FL-270: onStationSelectは保持（最寄駅機能）
            onStationSelect={handleStationSelect}
            onSubmit={handleSubmit}
            onCancel={cancelEditing}
          />
        </div>
      ) : (
        <TransporterList />
      )}

      {/* 詳細表示モーダル */}
      <LandTransporterDetailModal
        transporter={selectedTransporter}
        onClose={() => setSelectedTransporter(null)}
        onEdit={startEditing}
        onDelete={handleDelete}
        vehicleTypes={vehicleTypes}
        canViewBusinessConstraints={canViewBusinessConstraints}
      />

      {/* 削除確認モーダル */}
      {deleteConfirm?.show && (
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50'>
          <div className='bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6'>
            <div className='flex items-center mb-4'>
              <TrashIcon className='h-6 w-6 text-red-600 dark:text-red-400 mr-3' />
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>削除の確認</h3>
            </div>
            <p className='text-sm text-gray-600 dark:text-gray-300 mb-6'>
              陸送員「{deleteConfirm.transporter.name}
              」さんを削除しますか？この操作は取り消せません。
            </p>
            <div className='flex justify-end space-x-3'>
              <Button variant='secondary' onClick={() => setDeleteConfirm(null)} size='md'>
                キャンセル
              </Button>
              <Button variant='danger' onClick={confirmDelete} size='md'>
                <TrashIcon className='h-4 w-4 mr-2' />
                削除する
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
